import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Upload, Trash2, MapPin, Layers, Power, Save, Loader2, FileText, ArrowUpDown, CheckSquare, Square } from 'lucide-react';
import MapLibreCampMap from './MapLibreCampMap';

const API = process.env.REACT_APP_BACKEND_URL;

// Admin panel for the externally-built MapLibre map package.
// Responsibilities:
//   1. Upload the ZIP exported from the map builder tool.
//   2. Toggle the custom map on/off (fallback to the legacy Leaflet editor).
//   3. List parcels found in the package and let admin map each one to a
//      Beds24 room_id and an optional zone_name.
//   4. Show a live preview of the package using MapLibreCampMap (in admin
//      mode, properties=[] so nothing is "bookable" — just visual review).
export default function CustomMapManager({ token, propertyConfigs = [] }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [status, setStatus] = useState(null); // server side custom_map_settings
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [savingPid, setSavingPid] = useState(null);
  const [stickyZone, setStickyZone] = useState('');
  // Sort: "label-asc" (Parcela 1, 2, 10, …), "label-desc", "created" (file order)
  const [sortMode, setSortMode] = useState('label-asc');
  // Multi-select state — set of parcel_ids checked in the table
  const [selected, setSelected] = useState(() => new Set());
  const [bulkApplying, setBulkApplying] = useState(false);
  // {room_id: room_name} from Beds24 — populated lazily for the dropdowns so
  // admins see "Parcela 17 · 690701" instead of bare IDs.
  const [roomNames, setRoomNames] = useState({});
  const [roomNamesLoading, setRoomNamesLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/api/camp-map/custom-status`),
        axios.get(`${API}/api/camp-map/custom-parcels`),
      ]);
      setStatus(s.data);
      setParcels(p.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  // Fetch Beds24 room names → so the parcel-room dropdown can show
  // "Parcela 17 · 690701" instead of bare numeric IDs. Endpoint is cached
  // server-side for 6h; admin can force a refresh via the button below.
  const loadRoomNames = async (forceRefresh = false) => {
    if (!token) return;
    setRoomNamesLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/admin/beds24/room-names${forceRefresh ? '?refresh=true' : ''}`,
        { headers },
      );
      setRoomNames(res.data?.room_names || {});
    } catch {
      // Silently ignore — UI falls back to showing just room_id.
    } finally {
      setRoomNamesLoading(false);
    }
  };
  useEffect(() => { loadRoomNames(false); /* eslint-disable-next-line */ }, [token]);

  // Existing room_ids already assigned somewhere — disable them in other selects
  // so admin can't accidentally bind the same Beds24 room to two parcels.
  const usedRoomIds = useMemo(() => {
    const m = new Map();
    parcels.forEach(p => { if (p.room_id) m.set(String(p.room_id), p.parcel_id); });
    return m;
  }, [parcels]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/admin/camp-map/upload-package`, fd, { headers });
      if (res.data.ok) await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setUploadError(typeof detail === 'string' ? detail : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const toggleActive = async (active) => {
    await axios.post(`${API}/api/admin/camp-map/custom-toggle`, { active }, { headers });
    await refresh();
  };

  const updateParcel = async (parcel_id, patch) => {
    setSavingPid(parcel_id);
    try {
      await axios.put(`${API}/api/admin/camp-map/custom-parcels/${parcel_id}`, patch, { headers });
      setParcels(prev => prev.map(p => p.parcel_id === parcel_id ? { ...p, ...patch } : p));
    } finally {
      setSavingPid(null);
    }
  };

  // Natural sort: extract the first integer from the label and compare numerically
  // so "Parcela 2" < "Parcela 10". Falls back to string compare if no number.
  const sortedParcels = useMemo(() => {
    if (sortMode === 'created') return parcels;
    const numOf = (s) => {
      const m = String(s ?? '').match(/-?\d+/);
      return m ? parseInt(m[0], 10) : Number.POSITIVE_INFINITY;
    };
    const arr = [...parcels].sort((a, b) => {
      const na = numOf(a.label);
      const nb = numOf(b.label);
      if (na !== nb) return na - nb;
      return String(a.label || '').localeCompare(String(b.label || ''), undefined, { numeric: true });
    });
    return sortMode === 'label-desc' ? arr.reverse() : arr;
  }, [parcels, sortMode]);

  // Selection helpers — operate on the currently displayed (sorted) list so
  // "select all" only checks the visible parcels.
  const allSelected = sortedParcels.length > 0 && sortedParcels.every(p => selected.has(p.parcel_id));
  const someSelected = selected.size > 0 && !allSelected;
  const toggleSelected = (pid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelected(prev => {
      if (sortedParcels.every(p => prev.has(p.parcel_id))) return new Set();
      return new Set(sortedParcels.map(p => p.parcel_id));
    });
  };
  const clearSelection = () => setSelected(new Set());

  // Apply current `stickyZone` to all selected parcels in one bulk request.
  const applyZoneToSelected = async () => {
    const zone = stickyZone.trim();
    const ids = Array.from(selected);
    if (!zone || ids.length === 0) return;
    setBulkApplying(true);
    try {
      await axios.post(
        `${API}/api/admin/camp-map/custom-parcels/bulk`,
        { parcel_ids: ids, patch: { zone_name: zone } },
        { headers },
      );
      setParcels(prev => prev.map(p => ids.includes(p.parcel_id) ? { ...p, zone_name: zone } : p));
      clearSelection();
    } finally {
      setBulkApplying(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center" style={{ color: 'var(--color-text-muted)' }}>Učitavanje…</div>;
  }

  const hasPackage = !!status?.style_url;
  const active = !!status?.active;
  const uploadedDate = status?.uploaded_at
    ? new Date(status.uploaded_at).toLocaleString('hr-HR')
    : null;

  return (
    <div className="space-y-4" data-testid="custom-map-manager">
      {/* Header / status */}
      <div className="bg-white rounded-lg border p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-semibold text-lg flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <Layers size={20} /> Custom Mapa (Map Builder Package)
          </h3>
          {hasPackage && (
            <button onClick={() => toggleActive(!active)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
              style={active
                ? { background: 'var(--color-primary)', color: '#fff' }
                : { background: 'var(--color-background)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)' }}
              data-testid="toggle-custom-map">
              <Power size={14} />
              {active ? 'Aktivna — koristi se' : 'Neaktivna — koristi se Leaflet'}
            </button>
          )}
        </div>

        {hasPackage && (
          <div className="text-xs flex flex-wrap items-center gap-x-4 gap-y-1"
            style={{ color: 'var(--color-text-muted)' }}>
            <span><FileText size={11} className="inline mr-1" /> {status.style_url}</span>
            {uploadedDate && <span>Uploadano: {uploadedDate}</span>}
            <span>Parcela u paketu: <strong>{status.parcel_count ?? parcels.length}</strong></span>
          </div>
        )}

        {/* Upload zone */}
        <label
          className="block border-2 border-dashed rounded-lg p-5 cursor-pointer transition-colors"
          style={{
            borderColor: uploading ? 'var(--color-primary)' : 'var(--color-border)',
            background: uploading ? 'var(--color-primary-light)' : 'var(--color-background-light)',
          }}
          data-testid="upload-zone">
          <input
            type="file"
            accept=".zip,application/zip"
            className="hidden"
            disabled={uploading}
            onChange={e => handleUpload(e.target.files?.[0])}
            data-testid="upload-input"
          />
          <div className="flex items-center justify-center gap-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            {uploading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
            <span>
              {uploading
                ? 'Uploadam i sinkroniziram parcele…'
                : hasPackage
                  ? 'Klikni za upload novog paketa (zip)'
                  : 'Klikni za upload Map Builder paketa (.zip)'}
            </span>
          </div>
          <p className="text-xs mt-2 text-center" style={{ color: 'var(--color-text-muted)' }}>
            ZIP mora sadržavati <code>style.json</code> + sprite-ove. Maksimalno 50 MB.
          </p>
        </label>
        {uploadError && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700"
            data-testid="upload-error">
            {uploadError}
          </div>
        )}
      </div>

      {/* Sticky zone helper */}
      {hasPackage && parcels.length > 0 && (
        <div className="bg-white rounded-lg p-3 flex flex-wrap items-center gap-3 border"
          style={{ borderColor: 'var(--color-border)' }}
          data-testid="sticky-zone-bar">
          <Layers size={16} style={{ color: 'var(--color-primary)' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Aktivna zona:
          </span>
          <input type="text" value={stickyZone} onChange={e => setStickyZone(e.target.value)}
            placeholder="npr. Prvi red do mora"
            className="border rounded px-2 py-1 text-sm flex-1 min-w-[180px]"
            data-testid="sticky-zone-input" />
          <button
            onClick={async () => {
              if (!stickyZone.trim()) return;
              const targets = parcels.filter(p => !p.zone_name);
              if (targets.length === 0) return;
              setBulkApplying(true);
              try {
                await axios.post(
                  `${API}/api/admin/camp-map/custom-parcels/bulk`,
                  { parcel_ids: targets.map(p => p.parcel_id), patch: { zone_name: stickyZone.trim() } },
                  { headers },
                );
                setParcels(prev => prev.map(p => !p.zone_name ? { ...p, zone_name: stickyZone.trim() } : p));
              } finally { setBulkApplying(false); }
            }}
            disabled={bulkApplying || !stickyZone.trim()}
            className="px-3 py-1.5 text-white rounded text-sm disabled:opacity-50"
            style={{ background: 'var(--color-primary)' }}
            data-testid="apply-zone-to-empty-btn">
            Primijeni na parcele bez zone
          </button>
          <button
            onClick={applyZoneToSelected}
            disabled={bulkApplying || !stickyZone.trim() || selected.size === 0}
            className="px-3 py-1.5 rounded text-sm font-medium disabled:opacity-50"
            style={{ background: 'var(--color-primary-light)', color: 'var(--color-primary)', border: '1px solid var(--color-primary)' }}
            data-testid="apply-zone-to-selected-btn">
            {bulkApplying ? <Loader2 size={14} className="animate-spin inline mr-1" /> : null}
            Primijeni na odabrane ({selected.size})
          </button>
        </div>
      )}

      {/* Parcel mapping table */}
      {hasPackage && parcels.length > 0 && (
        <div className="bg-white rounded-lg border" data-testid="parcel-mapping-table">
          <div className="p-3 border-b flex flex-wrap items-center gap-3" style={{ borderColor: 'var(--color-border)' }}>
            <div className="flex-1 min-w-[200px]">
              <h4 className="font-semibold text-sm flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <MapPin size={14} /> Mapping parcela ({parcels.length})
                {selected.size > 0 && (
                  <span className="ml-2 px-2 py-0.5 rounded-full text-[11px] font-semibold"
                    style={{ background: 'var(--color-primary-light)', color: 'var(--color-primary)' }}
                    data-testid="selected-count-badge">
                    {selected.size} odabrano
                  </span>
                )}
              </h4>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                Poveži svaku parcelu iz tvog map builder paketa s točnim Beds24 roomom i postavi zonu.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <ArrowUpDown size={14} style={{ color: 'var(--color-text-muted)' }} />
              <label className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Sortiraj:</label>
              <select
                value={sortMode}
                onChange={e => setSortMode(e.target.value)}
                className="border rounded px-2 py-1 text-sm bg-white"
                data-testid="parcel-sort-select">
                <option value="label-asc">Po broju (1 → 99)</option>
                <option value="label-desc">Po broju (99 → 1)</option>
                <option value="created">Redoslijed kreiranja</option>
              </select>
              <button
                onClick={() => loadRoomNames(true)}
                disabled={roomNamesLoading}
                className="text-xs underline ml-2 flex items-center gap-1 disabled:opacity-50"
                style={{ color: 'var(--color-primary)' }}
                title="Povuci najnovija imena soba iz Beds24"
                data-testid="refresh-room-names-btn">
                {roomNamesLoading
                  ? <Loader2 size={12} className="animate-spin" />
                  : <FileText size={12} />}
                Osvježi Beds24 imena
              </button>
              {selected.size > 0 && (
                <button onClick={clearSelection}
                  className="text-xs underline ml-2"
                  style={{ color: 'var(--color-text-muted)' }}
                  data-testid="clear-selection-btn">
                  Odznači sve
                </button>
              )}
            </div>
          </div>
          {/* Select-all row */}
          <div className="px-3 py-2 border-b flex items-center gap-3 text-xs"
            style={{ borderColor: 'var(--color-border)', background: 'var(--color-background-light)' }}>
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-2 font-semibold"
              style={{ color: 'var(--color-text-primary)' }}
              data-testid="select-all-toggle">
              {allSelected ? <CheckSquare size={16} style={{ color: 'var(--color-primary)' }} />
                : someSelected ? <CheckSquare size={16} style={{ color: 'var(--color-text-muted)' }} />
                : <Square size={16} style={{ color: 'var(--color-text-muted)' }} />}
              <span>{allSelected ? 'Odznači sve vidljive' : 'Označi sve vidljive'}</span>
            </button>
          </div>
          <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
            {sortedParcels.map(p => {
              const isChecked = selected.has(p.parcel_id);
              return (
              <div key={p.parcel_id}
                className="p-3 flex flex-wrap items-center gap-3"
                style={isChecked ? { background: 'var(--color-primary-light)' } : undefined}
                data-testid={`parcel-row-${p.parcel_id}`}>
                <button
                  onClick={() => toggleSelected(p.parcel_id)}
                  className="shrink-0"
                  aria-label={isChecked ? 'Odznači parcelu' : 'Označi parcelu'}
                  data-testid={`parcel-checkbox-${p.parcel_id}`}>
                  {isChecked
                    ? <CheckSquare size={18} style={{ color: 'var(--color-primary)' }} />
                    : <Square size={18} style={{ color: 'var(--color-text-muted)' }} />}
                </button>
                <div className="flex items-center gap-2 min-w-[120px]">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded text-white text-xs font-bold"
                    style={{ background: p.fill || '#22C55E' }}>
                    {p.label || '?'}
                  </span>
                  <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    <div style={{ color: 'var(--color-text-primary)' }} className="font-medium">Parcela {p.label}</div>
                    <div title={p.parcel_id} className="font-mono text-[10px]">
                      {String(p.parcel_id).slice(0, 8)}…
                    </div>
                  </div>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="text-xs block mb-1" style={{ color: 'var(--color-text-muted)' }}>Soba (Beds24)</label>
                  <select
                    value={p.room_id || ''}
                    onChange={e => updateParcel(p.parcel_id, { room_id: e.target.value })}
                    className="w-full border rounded px-2 py-1.5 text-sm bg-white"
                    data-testid={`room-select-${p.parcel_id}`}>
                    <option value="">— Nije povezana —</option>
                    {propertyConfigs.length === 0 && (
                      <option disabled>Nema konfiguriranih properties</option>
                    )}
                    {propertyConfigs.map(pc => {
                      // Sort room_ids by their Beds24 name (natural numeric) so
                      // the dropdown lists Parcela 1, 2, 3 … in order. Fallback
                      // to the room_id when no name is cached yet.
                      const rids = [...(pc.room_ids || [])].sort((a, b) => {
                        const an = String(roomNames[a] || a);
                        const bn = String(roomNames[b] || b);
                        return an.localeCompare(bn, undefined, { numeric: true, sensitivity: 'base' });
                      });
                      return (
                      <optgroup key={pc.property_id} label={`${pc.property_name} (${pc.property_id})`}>
                        {rids.map(rid => {
                          const owner = usedRoomIds.get(String(rid));
                          const used = owner && owner !== p.parcel_id;
                          const name = roomNames[String(rid)];
                          const label = name ? `${name} · ${rid}` : String(rid);
                          return (
                            <option key={rid} value={rid} disabled={used}>
                              {label}{used ? ' · već u upotrebi' : ''}
                            </option>
                          );
                        })}
                      </optgroup>
                      );
                    })}
                  </select>
                </div>
                <div className="flex-1 min-w-[180px]">
                  <label className="text-xs block mb-1" style={{ color: 'var(--color-text-muted)' }}>Zona</label>
                  <input
                    type="text"
                    value={p.zone_name || ''}
                    onChange={e => updateParcel(p.parcel_id, { zone_name: e.target.value })}
                    placeholder="npr. Prvi red do mora"
                    className="w-full border rounded px-2 py-1.5 text-sm"
                    data-testid={`zone-input-${p.parcel_id}`}
                  />
                </div>
                {savingPid === p.parcel_id && (
                  <Loader2 size={14} className="animate-spin" style={{ color: 'var(--color-primary)' }} />
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Live preview */}
      {hasPackage && (
        <div className="bg-white rounded-lg border p-3" style={{ borderColor: 'var(--color-border)' }}>
          <h4 className="font-semibold text-sm mb-2 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <MapPin size={14} /> Live preview
          </h4>
          <div style={{ height: '500px' }}>
            <MapLibreCampMap
              styleUrl={status.style_url}
              center={status.center}
              zoom={status.zoom}
              minZoom={status.min_zoom}
              maxZoom={status.max_zoom}
              properties={[]}
              onParcelClick={() => {}}
              mobileMode={false}
              showLegend={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}
